import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { authorButton, authorInput, authorPanel, downloadText } from './qmsAuthoring';

export function canManageDraftJson(user) {
  const metadata = user?.publicMetadata || {};
  return [metadata.role, metadata.roles].flat().some(r => typeof r === 'string'
    && (['admin','quality-manager','production-engineer'].includes(r.trim().toLowerCase()) || r.trim().toLowerCase().startsWith('service')));
}

export function draftAuthoringEnvelope(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value) || value.schemaVersion !== 1) throw new Error('Use a schemaVersion 1 draft JSON object.');
  if (!Array.isArray(value.records) || !value.records.length || value.records.length > 50) throw new Error('Provide 1–50 records.');
  return { schemaVersion:1, records:value.records.map(r => ({ action:r.action, documentNumber:r.documentNumber,
    ...(r.expectedVersion !== undefined ? { expectedVersion:r.expectedVersion } : {}),
    ...(r.draftSuppliers !== undefined ? { draftSuppliers:r.draftSuppliers } : {}), changeSummary:r.changeSummary || '', content:r.content })) };
}

const OPERATIONAL_FIELDS = new Set(['documentNumber','calibrationStatus','verificationStatus','lastCalibratedAt','nextCalibrationDue',
  'lastVerifiedAt','nextVerificationDue','lastMaintainedAt','nextMaintenanceDue','inServiceDate','decommissionedAt']);
export function resourceEditContent(value) {
  return Object.fromEntries(Object.entries(value || {}).filter(([k]) => !OPERATIONAL_FIELDS.has(k)
    && !/History$/.test(k) && !['_id','__v','createdAt','updatedAt','createdBy','createdByName','updatedBy','updatedByName',
      'currentStatus','currentRevision','revisions','releasedSnapshots','revisionSnapshots','availableRevisions','snapshot','documentStatus'].includes(k)));
}

const DETAIL_PATH = { 'process-flows':'process', jigs:'jigs', equipment:'equipment' };
export default function ControlledDraftJson({ library, record, blocked = false, revision = '', onApplied, onBusyChange }) {
  const { getToken } = useAuth(); const { user } = useUser();
  const [text, setText] = useState(''); const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null); const [error, setError] = useState('');
  const [busy, setBusy] = useState(false); const busyRef = useRef(false);
  const validated = useRef(null); const fileSequence = useRef(0);
  const contextGeneration = useRef(0);
  const allowed = canManageDraftJson(user);
  const historical = !!revision || (record && record.currentStatus !== 'Draft');
  const invalidateContext = useCallback(() => {
    contextGeneration.current++; validated.current = null; fileSequence.current++;
  }, []);
  useEffect(() => {
    invalidateContext(); setPreview(null);
    return invalidateContext;
  }, [library, record?._id, record?.__v, blocked, revision, invalidateContext]);
  useEffect(() => { onBusyChange?.(busy); return () => onBusyChange?.(false); }, [busy, onBusyChange]);
  const change = value => { setText(value); validated.current = null; setPreview(null); setResult(null); setError(''); };

  async function exportJson(mode) {
    if (!allowed || busyRef.current || blocked || !record) return;
    const generation = contextGeneration.current;
    busyRef.current = true; setBusy(true); setError('');
    try {
      const token = await getToken();
      const query = revision ? `?revision=${encodeURIComponent(revision)}` : '';
      const res = await window.fetch(`${process.env.REACT_APP_API_URL}/quality/${library}/${record._id}/export.json${query}`, { headers:{ Authorization:`Bearer ${token}` } });
      const data = await res.json();
      if (generation !== contextGeneration.current) return;
      if (!res.ok) throw new Error(data.message || data.error || 'Could not export saved record');
      const authoring = draftAuthoringEnvelope(data);
      if (mode === 'load') { fileSequence.current++; change(JSON.stringify(authoring,null,2)); }
      else downloadText(JSON.stringify(mode === 'backup' ? data : authoring,null,2),`${record.documentNumber} ${record.currentRevision} ${mode}.json`);
    } catch (err) { if (generation === contextGeneration.current) setError(err.message); }
    finally { busyRef.current = false; setBusy(false); }
  }

  async function send(body) {
    const token = await getToken();
    const res = await window.fetch(`${process.env.REACT_APP_API_URL}/quality/${library}/draft-json`, {
      method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' }, body:JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.ok) { const err = new Error(data.message || data.error || (data.errors || []).join('; ') || 'Draft update failed'); err.preview = data; throw err; }
    return data;
  }

  async function validate() {
    if (!allowed || historical || blocked || busyRef.current) return;
    const generation = contextGeneration.current;
    busyRef.current = true; setBusy(true); setError(''); validated.current = null; setPreview(null); setResult(null);
    try {
      const parsed = JSON.parse(text);
      if (parsed.library && parsed.library !== library) throw new Error('This JSON belongs to a different library.');
      const body = { ...draftAuthoringEnvelope(parsed), requestId:window.crypto.randomUUID(), dryRun:true };
      const data = await send(body);
      if (generation !== contextGeneration.current) return;
      setPreview(data);
      if (!(data.errors || []).length && (data.records || []).length === body.records.length
        && data.records.every(r => ['create','update','unchanged'].includes(r.action) && !(r.errors || []).length)) validated.current = body;
    } catch (err) { if (generation === contextGeneration.current) { setError(err.message); if (err.preview) setPreview(err.preview); } }
    finally { busyRef.current = false; setBusy(false); }
  }

  async function apply() {
    if (!allowed || historical || blocked || busyRef.current || !validated.current) return;
    const generation = contextGeneration.current;
    busyRef.current = true; setBusy(true); setError('');
    try {
      const data = await send({ ...validated.current, dryRun:false });
      if (generation !== contextGeneration.current) return;
      validated.current = null; setPreview(null); setResult(data); setText('');
      onApplied?.(data);
    } catch (err) { if (generation === contextGeneration.current) { setError(err.message); if (err.preview) setPreview(err.preview); } }
    finally { busyRef.current = false; setBusy(false); }
  }

  async function readFile(event) {
    const file = event.target.files?.[0]; const sequence = ++fileSequence.current;
    if (!file) return;
    change('');
    if (file.size > 4 * 1024 * 1024) { setError('The JSON file must be smaller than 4 MB.'); return; }
    try { const value = await file.text(); if (sequence === fileSequence.current) change(value); }
    catch { if (sequence === fileSequence.current) setError('Could not read this file.'); }
  }

  if (!allowed) return null;
  return <details style={authorPanel}>
    <summary style={{ cursor:'pointer', fontWeight:700 }}>Draft JSON · backup, validate and apply</summary>
    <p style={{ fontSize:13, color:'var(--ni-text-muted)' }}>Update complete draft records, including process metadata and stations, in one save. Validate first. Saved revision, approval and operational history remain controlled by QMS.</p>
    {record && <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
      <button type="button" style={authorButton} disabled={busy || blocked} onClick={() => exportJson('backup')}>Download saved backup</button>
      <button type="button" style={authorButton} disabled={busy || blocked} onClick={() => exportJson('authoring')}>Download editable JSON</button>
      {!historical && <button type="button" style={authorButton} disabled={busy || blocked} onClick={() => exportJson('load')}>Load saved draft JSON</button>}
    </div>}
    {blocked && <p role="status">Save or cancel other edits before using draft JSON.</p>}
    {historical ? <p>This document view is read-only. Draft JSON changes cannot be applied here.</p> : <>
      <label>Draft JSON file <input type="file" accept=".json,application/json" disabled={busy || blocked} onChange={readFile} /></label>
      <label style={{ display:'block', marginTop:10 }}>Draft batch JSON<textarea aria-label="Draft batch JSON" rows={8} value={text} disabled={busy || blocked}
        onChange={e => { fileSequence.current++; change(e.target.value); }} style={{ ...authorInput, marginTop:6, fontFamily:'monospace' }} /></label>
      <div style={{ display:'flex', gap:8, marginTop:10 }}>
        <button type="button" style={authorButton} disabled={busy || blocked || !text.trim()} onClick={validate}>{busy ? 'Working…' : 'Validate draft JSON'}</button>
        <button type="button" style={authorButton} disabled={busy || blocked || !validated.current} onClick={apply}>Apply validated drafts</button>
      </div>
    </>}
    {error && <p role="alert" style={{ color:'#EC2124' }}>{error}</p>}
    {(preview || result) && <div role="status" style={{ fontSize:13 }}>
      {result && <p>Draft JSON applied. No engineering release was performed.</p>}
      <ul>{((preview || result).records || []).map((r,i) => <li key={i}>
        <strong>{r.documentNumber}</strong> — {r.action}{r.revision && ` (${r.revision})`}
        {!!r.changedFields?.length && <div>Changed fields: {r.changedFields.join(', ')}</div>}
        {[...(r.errors || []), ...(r.warnings || [])].map((m,j) => <div key={j}>{m}</div>)}
        {result && r.id && <a href={`/quality/${DETAIL_PATH[library]}/${r.id}`}>Open document</a>}
      </li>)}</ul>
    </div>}
  </details>;
}
